import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MapPin, Globe2, ArrowRight } from "lucide-react";

// Geographically placed on the India outline (equirectangular frame: 67–99°E, 5–37.5°N)
const HUB = { id: "hyderabad", label: "Hyderabad Hub", x: 36, y: 62 };
const INDIA_CITIES = [
  { id: "delhi", label: "Delhi", x: 32, y: 27 },
  { id: "ahmedabad", label: "Ahmedabad", x: 17, y: 45 },
  { id: "mumbai", label: "Mumbai", x: 18, y: 57, lx: -16 },
  { id: "pune", label: "Pune", x: 21, y: 58, lx: 18 },
  { id: "kolkata", label: "Kolkata", x: 67, y: 46 },
  { id: "chennai", label: "Chennai", x: 42, y: 75, lx: 18 },
  { id: "bengaluru", label: "Bengaluru", x: 33, y: 76, lx: -18 },
];

const GLOBAL_ROUTES = [
  { id: "usa", label: "USA", x: 82, y: 20 },
  { id: "uk", label: "UK", x: 62, y: 12 },
  { id: "uae", label: "UAE", x: 46, y: 34 },
  { id: "canada", label: "Canada", x: 88, y: 8 },
  { id: "australia", label: "Australia", x: 86, y: 80 },
  { id: "singapore", label: "Singapore", x: 68, y: 68 },
  { id: "europe", label: "Europe", x: 58, y: 6 },
  { id: "middle-east", label: "Middle East", x: 40, y: 28 },
];

function IndiaCoverageMap() {
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#030B18]/80" data-testid="domestic-india-map">
      <div className="absolute inset-0 hero-grid opacity-50" />
      {/* Map frame keeps the SVG's own aspect ratio so city dots align geographically */}
      <div className="absolute inset-y-1 left-1/2 -translate-x-1/2" style={{ aspectRatio: "1486 / 1734" }}>
        <img src="/maps/india.svg" alt="India coverage map" className="absolute inset-0 h-full w-full opacity-95" />
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <defs>
            <linearGradient id="rg-domestic" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF9933" /><stop offset="100%" stopColor="#12C8F5" />
            </linearGradient>
          </defs>
          {INDIA_CITIES.map((n, i) => (
            <path key={n.id} d={`M ${HUB.x} ${HUB.y} Q ${(HUB.x + n.x) / 2} ${Math.min(HUB.y, n.y) - 8 - (i % 3) * 3}, ${n.x} ${n.y}`}
              fill="none" stroke="url(#rg-domestic)" strokeWidth="0.5" vectorEffect="non-scaling-stroke"
              className="route-dash" style={{ animationDuration: `${5 + i * 1.3}s` }} />
          ))}
        </svg>
        {/* origin hub */}
        <div className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${HUB.x}%`, top: `${HUB.y}%` }} data-testid="domestic-map-node-hyderabad">
          <span className="node-glow flex h-9 w-9 items-center justify-center rounded-full border-2 border-[#FF9933] bg-[#071B3A]">
            <MapPin className="h-4 w-4 text-[#FF9933]" />
          </span>
          <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-[#071B3A] px-2 py-0.5 text-[9px] font-bold text-white ring-1 ring-white/15">{HUB.label}</span>
        </div>
        {INDIA_CITIES.map((n, i) => (
          <div key={n.id} className="group absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${n.x}%`, top: `${n.y}%` }}
            data-testid={`domestic-map-node-${n.id}`}>
            <span className="pulse-dot block h-3 w-3 rounded-full border-2 border-[#030B18] bg-[#FF9933]" style={{ animationDelay: `${i * 0.35}s` }} />
            <span className="absolute left-1/2 top-full mt-1 whitespace-nowrap text-[9px] font-semibold text-slate-300 opacity-80 transition-opacity group-hover:opacity-100" style={{ transform: `translateX(calc(-50% + ${n.lx || 0}px))` }}>{n.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RouteMap({ origin, nodes, accent, testPrefix, originLabel, originTestId, originImage }) {
  return (
    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#030B18]/80">
      <div className="absolute inset-0 hero-grid opacity-50" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        <defs>
          <linearGradient id={`rg-${testPrefix}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent} /><stop offset="100%" stopColor="#12C8F5" />
          </linearGradient>
        </defs>
        {nodes.map((n, i) => (
          <path key={n.id} d={`M ${origin.x} ${origin.y} Q ${(origin.x + n.x) / 2} ${Math.min(origin.y, n.y) - 12 - (i % 3) * 4}, ${n.x} ${n.y}`}
            fill="none" stroke={`url(#rg-${testPrefix})`} strokeWidth="0.5" vectorEffect="non-scaling-stroke"
            className="route-dash" style={{ animationDuration: `${5 + i * 1.3}s` }} />
        ))}
      </svg>
      {/* origin node */}
      <div className="absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${origin.x}%`, top: `${origin.y}%` }} data-testid={originTestId}>
        {originImage ? (
          <img src={originImage} alt={originLabel} className="h-14 w-auto drop-shadow-[0_0_16px_rgba(16,185,129,0.55)]" />
        ) : (
          <span className="node-glow flex h-9 w-9 items-center justify-center rounded-full border-2 bg-[#071B3A]" style={{ borderColor: accent }}>
            <MapPin className="h-4 w-4" style={{ color: accent }} />
          </span>
        )}
        <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-[#071B3A] px-2 py-0.5 text-[9px] font-bold text-white ring-1 ring-white/15">{originLabel}</span>
      </div>
      {nodes.map((n, i) => (
        <div key={n.id} className="group absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${n.x}%`, top: `${n.y}%` }}
          data-testid={`${testPrefix}-node-${n.id}`}>
          <span className="pulse-dot block h-3 w-3 rounded-full border-2 border-[#030B18]" style={{ background: accent, animationDelay: `${i * 0.35}s` }} />
          <span className="absolute left-1/2 top-full mt-1 -translate-x-1/2 whitespace-nowrap text-[9px] font-semibold text-slate-300 opacity-80 transition-opacity group-hover:opacity-100">{n.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function CoverageMaps() {
  return (
    <section className="relative overflow-hidden bg-[#071B3A] py-20 sm:py-24" data-testid="coverage-section">
      <div className="mx-auto max-w-7xl px-5">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }} className="text-center">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-shipany-cyan">Domestic + International</span>
          <h2 className="font-display mt-3 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">From Your Warehouse to Anywhere.</h2>
        </motion.div>
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}>
            <IndiaCoverageMap />
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-300"><span className="text-[#FF9933]">Pan-India</span> — metro to remote pincodes</p>
              <Link to="/signup" data-testid="ship-domestic-btn" className="group flex items-center gap-2 rounded-xl bg-[#FF9933] px-5 py-2.5 font-display text-xs font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_26px_rgba(255,153,51,0.4)]">
                SHIP DOMESTIC <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }}>
            <RouteMap origin={{ x: 22, y: 52 }} nodes={GLOBAL_ROUTES} accent="#10B981" testPrefix="global-map" originLabel="India" originTestId="global-map-node-india" originImage="/maps/india.svg" />
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm font-semibold text-slate-300"><span className="text-[#10B981]">Global reach</span> — USA, UK, UAE, EU & more</p>
              <Link to="/signup" data-testid="ship-international-btn" className="group flex items-center gap-2 rounded-xl bg-[#10B981] px-5 py-2.5 font-display text-xs font-bold text-[#030B18] transition-all hover:shadow-[0_8px_26px_rgba(16,185,129,0.4)]">
                <Globe2 className="h-3.5 w-3.5" /> SHIP INTERNATIONAL
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
